import type { Role } from '@/types/auth'
import { ROLE_LEVEL } from '@/types/auth'
import { ROUTES } from './routes'

/**
 * Defines the minimum role required to access each route.
 *
 * Routes NOT listed here are accessible by any authenticated user (USER+).
 * Public routes (login, force-change-password) don't need listing — they
 * are handled outside the private layout.
 *
 * Hierarchy: USER (0) < LEADER (1) < TECHNICIAN (2) < SSMA_MANAGER (3) < MANAGER (4) < ADMIN (5)
 */
export const ROUTE_PERMISSIONS: Partial<Record<string, Role>> = {
	// ─── LEADER ────────────────────────────────────────────────
	[ROUTES.DASHBOARD.COMMITMENT_MATRIX_DASHBOARD]: 'LEADER',
	[ROUTES.ADMINISTRATION.KPI_METRICS]: 'LEADER',
	[ROUTES.ADMINISTRATION.KPI_METRICS_TAB]: 'LEADER',

	// ─── TECHNICIAN ────────────────────────────────────────────
	[ROUTES.ADMINISTRATION.INDEX]: 'TECHNICIAN',
	[ROUTES.ADMINISTRATION.WHATS_NEW]: 'TECHNICIAN',
	[ROUTES.ADMINISTRATION.FIX_INSPECTION]: 'TECHNICIAN',
	[ROUTES.ADMINISTRATION.FIX_INSPECTION_FORM]: 'TECHNICIAN',

	// ─── SSMA_MANAGER ──────────────────────────────────────────
	[ROUTES.ADMINISTRATION.COMMITMENT_MATRIX]: 'SSMA_MANAGER',

	// ─── ADMIN ─────────────────────────────────────────────────
	[ROUTES.ADMINISTRATION.NOTIFICATIONS]: 'ADMIN',
	[ROUTES.ADMINISTRATION.NOTIFICATIONS_FORM]: 'ADMIN',
	[ROUTES.ADMINISTRATION.QUESTIONNAIRES]: 'ADMIN',
	[ROUTES.ADMINISTRATION.QUESTIONNAIRES_FORM]: 'ADMIN',
	[ROUTES.ADMINISTRATION.BRANCHES_CONTRACTS]: 'ADMIN',
	[ROUTES.ADMINISTRATION.BRANCHES_CONTRACTS_FORM]: 'ADMIN',
	[ROUTES.ADMINISTRATION.BADGES]: 'ADMIN',
	[ROUTES.ADMINISTRATION.TYPOLOGY_RISKS]: 'ADMIN',
	[ROUTES.ADMINISTRATION.TYPOLOGY_RISKS_FORM]: 'ADMIN',
	[ROUTES.ADMINISTRATION.CLASSIFICATIONS]: 'ADMIN',
	[ROUTES.ADMINISTRATION.CLASSIFICATIONS_FORM]: 'ADMIN',
	[ROUTES.ADMINISTRATION.UPLOAD_EMPLOYEES]: 'ADMIN',
	[ROUTES.ADMINISTRATION.SYNC_PROTHEUS]: 'ADMIN',
	[ROUTES.ADMINISTRATION.USERS]: 'ADMIN',
	[ROUTES.ADMINISTRATION.CONTRACTS]: 'ADMIN',
	[ROUTES.ADMINISTRATION.CUSTOMERS]: 'ADMIN',
	[ROUTES.ADMINISTRATION.DDS_MONTHLY_THEMES]: 'ADMIN',
	[ROUTES.ADMINISTRATION.ORG_OVERRIDES]: 'ADMIN',
	[ROUTES.ADMINISTRATION.SPHERA_EXPORT]: 'ADMIN',
	[ROUTES.ADMINISTRATION.SPHERA_OPTIONS]: 'ADMIN',
	[ROUTES.ADMINISTRATION.SCORING]: 'ADMIN',
}

export const ROUTE_ROLE_ALLOWLIST: Partial<Record<string, Role[]>> = {
	[ROUTES.INSPECTIONS.DIRECTED.INDEX]: [
		'ADMIN',
		'SSMA_MANAGER',
		'MANAGER',
		'TECHNICIAN',
		'LEADER',
	],
	[ROUTES.INSPECTIONS.YELLOW_CARD.INDEX]: [
		'ADMIN',
		'SSMA_MANAGER',
		'MANAGER',
		'TECHNICIAN',
		'LEADER',
	],
	[ROUTES.INSPECTIONS.INTERACTION.INDEX]: [
		'ADMIN',
		'SSMA_MANAGER',
		'MANAGER',
		'TECHNICIAN',
		'LEADER',
	],
	[ROUTES.INSPECTIONS.SEE_AND_ACT.INDEX]: [
		'ADMIN',
		'SSMA_MANAGER',
		'MANAGER',
		'TECHNICIAN',
		'LEADER',
		'USER',
	],
	[ROUTES.INSPECTIONS.ACTIONS_IN_PROGRESS]: [
		'ADMIN',
		'SSMA_MANAGER',
		'MANAGER',
		'TECHNICIAN',
		'LEADER',
	],
	[ROUTES.DDS.INDEX]: [
		'ADMIN',
		'SSMA_MANAGER',
		'MANAGER',
		'TECHNICIAN',
		'LEADER',
	],
	// TODO: revisar se os papéis listados em ROUTE_ROLE_ALLOWLIST para o módulo de DDS estão corretos e se precisam ser atualizados conforme a política de acesso do backend.
	[ROUTES.DDS.EMOTIONS]: [
		'ADMIN',
		'SSMA_MANAGER',
		'MANAGER',
		'TECHNICIAN',
		'LEADER',
	],
}

/**
 * Minimum role for a whole module: the base route and every sub-route
 * (e.g. /occurrences, /occurrences/new, /occurrences/:id/investigation).
 */
/**
 * Matrículas (registration) liberadas para o módulo de Ocorrências além dos ADMIN.
 * O badge vem do backend com 4 caracteres de prefixo não utilizados (ex.: AB12101288 → 101288).
 */
const BADGE_PREFIX_LENGTH = 4
export const OCCURRENCES_ALLOWED_REGISTRATIONS: string[] = [
	'865635',
	'915975',
	'128893',
	'910589',
	'881891',
	'908317',
	'254655',
	'101288',
	'113506',
	'915973',
]

export function hasAllowedRegistration(
	badge: string | undefined,
	registrations: string[],
): boolean {
	if (!badge) return false
	const registration = badge.slice(BADGE_PREFIX_LENGTH)
	return registrations.includes(registration)
}

/**
 * Administração de campanhas: criar, editar e ver resultados é de ADMIN e SSMA_MANAGER (igual ao backend).
 * Vale para a rota base e as sub-rotas (/form, /:id/results).
 */
export const ROUTE_PREFIX_ROLE_ALLOWLIST: Partial<Record<string, Role[]>> = {
	[ROUTES.ADMINISTRATION.CAMPAIGNS]: ['ADMIN', 'SSMA_MANAGER'],
}

export const ROUTE_PREFIX_PERMISSIONS: Partial<Record<string, Role>> = {
	// Ocorrências: protótipo com dados mock, liberado só para ADMIN validar
	[ROUTES.OCCURRENCES.INDEX]: 'ADMIN',
	// Grupos do menu da Administração (/administration/menu/<grupo>): mesmo acesso da tela inicial
	[ROUTES.ADMINISTRATION.GROUP.replace('/:group', '')]: 'TECHNICIAN',
}

/**
 * Check whether the given role has access to a route.
 * If the route has no explicit permission mapping, all roles are allowed.
 */
export function hasRouteAccess(
	role: Role,
	path: string,
	badge?: string,
): boolean {
	for (const [prefix, prefixRole] of Object.entries(ROUTE_PREFIX_PERMISSIONS)) {
		if (!prefixRole) continue
		if (path === prefix || path.startsWith(`${prefix}/`)) {
			if (ROLE_LEVEL[role] >= ROLE_LEVEL[prefixRole]) return true
			return (
				prefix === ROUTES.OCCURRENCES.INDEX &&
				hasAllowedRegistration(badge, OCCURRENCES_ALLOWED_REGISTRATIONS)
			)
		}
	}

	for (const [prefix, roles] of Object.entries(ROUTE_PREFIX_ROLE_ALLOWLIST)) {
		if (roles && (path === prefix || path.startsWith(`${prefix}/`))) {
			return roles.includes(role)
		}
	}

	const allowedRoles = ROUTE_ROLE_ALLOWLIST[path]
	if (allowedRoles) return allowedRoles.includes(role)

	const requiredRole = ROUTE_PERMISSIONS[path]
	if (!requiredRole) return true // no restriction
	return ROLE_LEVEL[role] >= ROLE_LEVEL[requiredRole]
}

/**
 * Minimum role required for each KPI Metrics tab.
 * Tabs not listed here fall back to the KPI_METRICS_TAB route permission (LEADER).
 */
export const KPI_METRICS_TAB_PERMISSIONS: Record<string, Role> = {
	'ver-e-agir': 'LEADER',
	'acoes-consolidadas': 'LEADER',
	interacao: 'LEADER',
	calibracao: 'LEADER',
	direcionada: 'LEADER',
	'resposta-livre': 'LEADER',
	'cartao-amarelo': 'LEADER',
	dds: 'LEADER',
}

/**
 * Check whether the given role has access to a specific KPI Metrics tab.
 */
export function hasKpiMetricsTabAccess(role: Role, tab: string): boolean {
	const requiredRole = KPI_METRICS_TAB_PERMISSIONS[tab]
	if (!requiredRole) return true
	return ROLE_LEVEL[role] >= ROLE_LEVEL[requiredRole]
}
